/**
 * What a founder is told when a submission is refused (canon APP-009).
 *
 * Keyed on the **code**, for the reason `./bid-messages.ts` gives: core's own messages are
 * contract text written for every caller, and some of them describe conditions a founder can
 * do nothing about. The code is the stable part of the contract
 * (`contracts/API.md` § Seller submission shape).
 *
 * One rule is specific to this surface. **Nothing here may suggest the deal was filed.**
 * Core writes the deal in one statement and reads it back through the same projection the
 * list uses, so a refusal means no deal exists — and a founder who believes otherwise either
 * waits for a review that will never happen or submits again on top of nothing. Every
 * sentence below says the deal was not submitted, or says nothing about it at all.
 */
import { isNetworkError } from "../api/client";
import type { ApiError } from "../api/client";

const MESSAGES: Record<string, string> = {
  // `409`. Almost always a double-click or an impatient resubmit rather than a second deal,
  // so it points at the review already under way instead of asking for a retry.
  DUPLICATE_SUBMISSION:
    "You have already submitted this deal link, and Dealbid is still reviewing it. It is on your deals panel.",
  // `429`. Core's own message names the number, and the count is the actionable part — but
  // the sentence is worded here so it reads as a queue limit rather than as a rejection.
  SUBMISSION_LIMIT_REACHED:
    "You already have as many deals awaiting review as Dealbid accepts at once. Wait for one of those to be reviewed, then submit this one.",
  // The whole-request sentence for a field refusal. Each offending box carries core's own
  // message beside it, so this only has to point at them.
  VALIDATION_FAILED:
    "Dealbid didn't accept some of these details. The problems are beside each one, and nothing was submitted.",
  PAYLOAD_TOO_LARGE:
    "That's more text than Dealbid accepts for a deal. Shorten the longer fields and submit again.",
  MALFORMED_RESPONSE:
    "Dealbid didn't answer as expected, so it isn't clear whether this was submitted. Reload your desk before sending it again.",
};

/** The generic. Honest about the two things actually known: it failed, and retrying is fair. */
const GENERIC = "Dealbid couldn't submit this deal. Try again in a moment.";

const OFFLINE =
  "Dealbid couldn't be reached, so nothing was submitted. Check your connection and try again.";

export function messageForSubmissionError(error: ApiError): string {
  // A malformed body also carries a null status — the request completed and the answer was
  // unreadable — so the code is checked first. Telling a founder their connection dropped
  // when it did not would send them to restart a router that is working.
  if (error.code !== null) return MESSAGES[error.code] ?? GENERIC;
  return isNetworkError(error) ? OFFLINE : GENERIC;
}
